import { Amplify } from 'aws-amplify'
import { Hub, I18n } from 'aws-amplify/utils'
import { getAwsUserPoolId, getAwsUserPoolWebClientId } from 'ComponentLibrary/src/common/environment'
import { sendDebugInfoToSentry } from 'ComponentLibrary'

import '@aws-amplify/ui-react/styles.css'
import { translations } from '@aws-amplify/ui-react'

I18n.putVocabularies(translations)

const SUPPORTED_AUTH_LANGUAGES = ['de', 'en'] as const
type SupportedAuthLanguage = (typeof SUPPORTED_AUTH_LANGUAGES)[number]

function normalizeAuthLanguage(locale?: string | null): SupportedAuthLanguage {
    const language = locale?.toLowerCase().split(/[-_]/)[0]

    return SUPPORTED_AUTH_LANGUAGES.find((supportedLanguage) => supportedLanguage === language) ?? 'de'
}

// Some translations are not right
I18n.putVocabulariesForLanguage('de', {
    'Enter your Password': 'Geben Sie Ihr Passwort ein',
    'Enter your Email': 'Geben Sie Ihre E-Mail ein',
    'Enter your email': 'Geben Sie Ihre E-Mail ein',
    'Please confirm your Password': 'Bitte bestätigen Sie Ihr Passwort',
    'Reset Password': 'Passwort zurücksetzen',
    'Temporary password has expired and must be reset by an administrator.':
        'Ihr temporäres Passwort ist abgelaufen. Bitte kontaktieren Sie support@einwert.com um Ihnen ein neues initiales Passwort zuzusenden.',
    'User does not exist.':
        'Benutzer nicht gefunden. Bitte stellen Sie sicher, dass Sie Ihren Benutzernamen exakt so eingeben wie in der Begrüßungsemail angegeben. Achten Sie auch auf Groß- und Kleinschreibung. (Zum Beispiel: Max.Muster@example.com)',
    'Incorrect username or password.':
        'Benutzername oder Passwort falsch. Bitte stellen Sie sicher, dass Sie Ihren Benutzernamen exakt so eingeben wie in der Begrüßungsemail angegeben. Achten Sie auch auf Groß- und Kleinschreibung. (Zum Beispiel: Max.Muster@example.com)',
    'Account recovery requires verified contact information': 'Bitte verifizieren Sie Ihre E-Mail-Adresse',
    'username is required to resetPassword': 'Bitte geben Sie Ihre E-Mail-Adresse ein, um Ihr Passwort zurückzusetzen.',
    'Reset your password': 'Passwort zurücksetzen',
    Submit: 'Absenden',
    'Password must have at least 8 characters': 'Das Passwort muss mind. 8 Zeichen lang sein',
    'Password must have at least 8 characters.': 'Das Passwort muss mind. 8 Zeichen lang sein',
    'Password must be at least 8 characters': 'Das Passwort muss mind. 8 Zeichen lang sein',
    'Password must be at least 8 characters.': 'Das Passwort muss mind. 8 Zeichen lang sein',
    'Password not long enough': 'Das Passwort muss mind. 8 Zeichen lang sein',
    'Your passwords must match': 'Die Passwörter müssen übereinstimmen',
    'Username/client id combination not found.':
        'Benutzer nicht gefunden. Bitte stellen Sie sicher, dass Sie Ihren Benutzernamen exakt so eingeben wie in der Begrüßungsemail angegeben. Achten Sie auch auf Groß- und Kleinschreibung. (Zum Beispiel: Max.Muster@example.com)',
    'User password cannot be reset in the current state.': 'Bitte kontaktieren Sie support@einwert.com um Ihr Passwort zurückzusetzen',
    'User is disabled.': 'Benutzer ist deaktiviert. Bitte kontaktieren Sie support@einwert.com.',
})

I18n.setLanguage('de')

function setAmplifyAuthLanguage(locale?: string | null) {
    I18n.setLanguage(normalizeAuthLanguage(locale))
}

type ConfigureAmplifyOptions = {
    userPoolId?: string
    userPoolClientId?: string
}

const configureAmplify = ({
    userPoolId = getAwsUserPoolId(),
    userPoolClientId = getAwsUserPoolWebClientId(),
}: ConfigureAmplifyOptions = {}) => {
    Amplify.configure({
        Auth: {
            Cognito: {
                userPoolId,
                userPoolClientId,
            },
        },
    })
}

type HubPayload = {
    event: string
    message?: string
}

const listener = (data: { payload: HubPayload }) => {
    switch (data?.payload?.event) {
        case 'signIn_failure':
            sendDebugInfoToSentry(new AuthenticationError(data.payload?.message || 'User failed to sign in'))
            break
        case 'completeNewPassword_failure':
            sendDebugInfoToSentry(new AuthenticationError('User failed to complete new password'))
            break
        case 'autoSignIn_failure':
            sendDebugInfoToSentry(new AuthenticationError('User failed to auto sign in'))
            break
        case 'forgotPassword_failure':
            sendDebugInfoToSentry(new AuthenticationError('User failed to reset password'))
            break
    }
}

Hub.listen('auth', listener)

class AuthenticationError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'AuthenticationError'
    }
}

export { configureAmplify, setAmplifyAuthLanguage }
